import { DataTable, Text, Flex, Badge, Icon, Button, Link, Tooltip } from '@scale/scaleui-radix';
import {
  useGetFlaggedReviewers,
  useInvalidateFlaggedReviews,
} from 'react-scale/corp/views/qm/QMLLMWorkerPerformancePage/hooks';
import { startChatBulkAudit } from 'react-scale/components/audits/ChatBulkAudit/utils';
import {
  AttemptFeedbackEntry,
  DisputeEntry,
  DisputeDecision,
  DisputeStatus,
  FeedbackDisputeCategory,
} from '@scale/shared/types/chat/disputes';
import { useState, useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useLocation } from 'react-router-dom';
import { useGetDisputesEnabled } from 'react-scale/remotasks.com/components/Expert/Feedback/hooks';
import { faCopy, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import FunSkeleton from 'react-scale/components/quality/fun/FunSkeleton';

export const DISPUTE_AUDIT_BATCH_NAME = '[Individual Dispute Audit]';
interface DisputeTableRow {
  id: string;
  attemptId: string;
  attempter: string;
  attempterName?: string;
  attempterEmail?: string;
  numDisputesByAttempter?: number;
  numReviewedDisputesByAttempter?: number;
  numLostDisputesByAttempter?: number;
  reviewer: string;
  reviewerName?: string;
  reviewerEmail?: string;
  numDisputesAgainstReviewer?: number;
  numReviewedDisputesAgainstReviewer?: number;
  numWonDisputesAgainstReviewer?: number;
  disputeDate: Date | undefined;
  languageCode: string;
  category: string;
  justification: string;
  suggestedScore: string;
  supportingInstruction: string;
  status: DisputeStatus;
  decision?: DisputeDecision;
  attemptFeedbackEntry: AttemptFeedbackEntry;
  dispute: DisputeEntry;
  originalScore: string;
  taskAttemptLevel: string;
  reviewerFeedback: string;
}

export const ReviewerDisputesTable = ({ projectId }: { projectId: string }) => {
  const { data, isLoading, refetch } = useGetFlaggedReviewers({ projectId });
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  const enableFeedbackDisputesFeatureFlag = useUnleashFeatureFlagWithLoading(
    'feedback-disputes',
    { projectId },
    false,
  ).data;

  const { data: useGetDisputesEnabledData, isLoading: isLoadingDisputesEnabled } =
    useGetDisputesEnabled({
      projectId,
    });
  const disputesEnabledFromConfig =
    isLoadingDisputesEnabled || !useGetDisputesEnabledData
      ? false
      : useGetDisputesEnabledData.areDisputesEnabled;

  const enableFeedbackDisputes = disputesEnabledFromConfig || enableFeedbackDisputesFeatureFlag;

  const { mutate: invalidateFlaggedReviews, isLoading: isInvalidatingDispute } =
    useInvalidateFlaggedReviews({
      projectId,
      refetchData: refetch,
    });

  const flaggedReviewers = useMemo(() => data?.result, [data]);
  const reviewerData = useMemo(() => data?.reviewerData, [data]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const contributorId = params.get('contributorId') || undefined;

  const handleInvalidateDispute = (
    assignmentId: string,
    decision: DisputeDecision,
    suggestedScore?: number,
  ) => {
    invalidateFlaggedReviews({
      attemptIds: [assignmentId],
      decision,
      suggestedScore,
    });
  };

  const handleAuditDispute = async (dispute: DisputeTableRow) => {
    setIsAuditLoading(true);
    await startChatBulkAudit({
      projectId,
      auditName: `${DISPUTE_AUDIT_BATCH_NAME} ${projectId} auditing dispute ${dispute.id}`,
      taskIds: [dispute.attemptId],
    });
    setIsAuditLoading(false);
  };

  const NewScoreSelector = ({
    attemptId,
    originalScore,
  }: {
    attemptId: string;
    originalScore: number;
  }) => {
    return (
      <div>
        {Array.from({ length: 5 - originalScore }, (_, index) => {
          const score = originalScore + index + 1;

          return (
            <Button
              className="mr-1"
              variant="outline"
              key={score}
              onClick={() => handleInvalidateDispute(attemptId, DisputeDecision.ACCEPTED, score)}
            >
              {score}
            </Button>
          );
        })}
      </div>
    );
  };

  // Transform the grouped data into individual dispute rows
  const disputeRows = useMemo((): DisputeTableRow[] => {
    if (!flaggedReviewers) return [];

    const rows: DisputeTableRow[] = [];

    flaggedReviewers.forEach((attemptFeedback: AttemptFeedbackEntry) => {
      const disputes = attemptFeedback.disputes || [];
      if (contributorId && attemptFeedback.attemptedBy !== contributorId) {
        return;
      }

      const attempterInfo = reviewerData?.[attemptFeedback.attemptedBy];
      const languageCode =
        data?.attemptMetadata?.[attemptFeedback.assignmentId]?.languageCode || 'N/A';

      const dispute: DisputeEntry = disputes[disputes.length - 1];
      const reviewerInfo = reviewerData?.[dispute.reviewer];
      const disputeStatsByReviewer = attemptFeedback.disputeStatsByReviewer;
      const disputeStatsByAttempter = attemptFeedback.disputeStatsByAttempter;

      rows.push({
        id: `${attemptFeedback.assignmentId}-${dispute.reviewer}-${dispute.createdAt}`,
        attemptId: attemptFeedback.assignmentId,
        attempter: attemptFeedback.attemptedBy,
        attempterName: attempterInfo?.name,
        attempterEmail: attempterInfo?.email,
        numDisputesByAttempter: disputeStatsByAttempter?.numDisputes,
        numReviewedDisputesByAttempter: disputeStatsByAttempter?.numReviewedDisputes,
        numLostDisputesByAttempter: disputeStatsByAttempter?.numLostDisputes,
        reviewer: dispute.reviewer,
        reviewerName: reviewerInfo?.name,
        reviewerEmail: reviewerInfo?.email,
        numDisputesAgainstReviewer: disputeStatsByReviewer?.numDisputes,
        numReviewedDisputesAgainstReviewer: disputeStatsByReviewer?.numReviewedDisputes,
        numWonDisputesAgainstReviewer: disputeStatsByReviewer?.numLostDisputes,
        disputeDate: dispute.createdAt,
        languageCode,
        category: dispute.disputeCategory,
        justification: dispute.justification,
        suggestedScore: dispute.metadata?.suggestedScore || 'N/A',
        supportingInstruction: dispute.metadata?.supportingInstruction || 'N/A',
        status: dispute.status,
        decision: dispute.decision,
        attemptFeedbackEntry: attemptFeedback,
        originalScore: attemptFeedback.originalScore,
        taskAttemptLevel: attemptFeedback.taskAttemptLevel,
        reviewerFeedback: attemptFeedback.reviewerFeedback,
        dispute,
      });
    });

    return rows.sort((a, b) => {
      // Sort by dispute date, newest first
      if (!a.disputeDate && !b.disputeDate) return 0;
      if (!a.disputeDate) return 1;
      if (!b.disputeDate) return -1;
      return new Date(b.disputeDate).getTime() - new Date(a.disputeDate).getTime();
    });
  }, [flaggedReviewers, reviewerData, contributorId]);

  const columnHelper = DataTable.createColumnHelper<DisputeTableRow>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'rowSelection',
        size: 36,
        enableResizing: false,
        enableHiding: false,
        header: function headerComponent() {
          return (
            <Flex align="center" height="100%" justify="center" width="100%">
              <DataTable.ColumnHeaderSelect />
            </Flex>
          );
        },
        cell: function cellComponent(props: any) {
          return (
            <Flex align="center" height="100%" justify="center" width="100%">
              <DataTable.RowSelect row={props.row} />
            </Flex>
          );
        },
      }),
      columnHelper.accessor('attemptId', {
        id: 'attemptLink',
        size: 150,
        minSize: 150,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Task Attempt</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          return (
            <Flex justify="start" align="center" gap="2" className="h-full">
              <Text className="text-xs truncate">{row.attemptId}</Text>
              <CopyToClipboard
                text={row.attemptId}
                onCopy={value =>
                  toast(`Copied task ID (${value}) to clipboard!`, {
                    position: 'bottom-center',
                    autoClose: 1000,
                    progressStyle: { background: 'green' },
                  })
                }
              >
                <Icon size="1" icon={faCopy} label="Copy task ID" />
              </CopyToClipboard>
              <Link
                href={`/corp/genai-ops-hub/search/${row.attemptId}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
              >
                <Icon size="1" icon={faExternalLink} label="View task" />
              </Link>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('attempter', {
        id: 'attempterLinks',
        size: 250,
        enableColumnFilter: false,
        filterFn: 'includesString',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Attempter</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          return (
            <Flex direction="column" gap="2" className="w-full h-full" justify="center">
              <Text className="font-medium">{row.attempterName || 'Unknown'}</Text>
              <Flex align="center" gap="2">
                <Text className="text-sm text-gray-600 truncate">{row.attempter}</Text>
                <CopyToClipboard
                  text={row.attempter}
                  onCopy={value =>
                    toast(`Copied attempter ID (${value}) to clipboard!`, {
                      position: 'bottom-center',
                      autoClose: 1000,
                      progressStyle: { background: 'green' },
                    })
                  }
                >
                  <Icon size="1" icon={faCopy} label="Copy attempter ID" />
                </CopyToClipboard>
                <Link
                  href={`/corp/genai-ops-hub/projects/${projectId}/quality/contributor-quality/contributor-deep-dive?contributorId=${row.attempter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon size="1" icon={faExternalLink} label="View attempter" />
                </Link>
              </Flex>
              {row.attempterEmail && (
                <Text className="text-sm text-gray-500 truncate">{row.attempterEmail}</Text>
              )}
              <Text className="text-sm text-gray-500 truncate">
                {row.numLostDisputesByAttempter} disputes lost by attempter
              </Text>
              <Text className="text-sm text-gray-500 truncate">
                {row.numReviewedDisputesByAttempter} disputes by attempter reviewed
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('numDisputesByAttempter', {
        id: 'numDisputesByAttempter',
        size: 220,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>
                # of Disputes by Attempter
              </DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const numDisputesByAttempter = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Text className="text-sm max-h-[300px] overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap">
                {numDisputesByAttempter}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('reviewer', {
        id: 'reviewerInfo',
        size: 260,
        enableColumnFilter: false,
        filterFn: 'includesString',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Reviewer</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          return (
            <Flex direction="column" gap="2" className="w-full h-full" justify="center">
              <Text className="font-medium">{row.reviewerName || 'Unknown'}</Text>
              <Flex align="center" gap="2">
                <Text className="text-sm text-gray-600 truncate">{row.reviewer}</Text>
                <CopyToClipboard
                  text={row.reviewer}
                  onCopy={value =>
                    toast(`Copied reviewer ID (${value}) to clipboard!`, {
                      position: 'bottom-center',
                      autoClose: 1000,
                      progressStyle: { background: 'green' },
                    })
                  }
                >
                  <Icon size="1" icon={faCopy} label="Copy reviewer ID" />
                </CopyToClipboard>
                <Link
                  href={`/corp/genai-ops-hub/projects/${projectId}/quality/contributor-quality/contributor-deep-dive?contributorId=${row.reviewer}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon size="1" icon={faExternalLink} label="View reviewer" />
                </Link>
              </Flex>
              {row.reviewerEmail && (
                <Text className="text-sm text-gray-500 truncate">{row.reviewerEmail}</Text>
              )}
              <Text className="text-sm text-gray-500 truncate">
                {row.numWonDisputesAgainstReviewer} disputes lost by reviewer
              </Text>
              <Text className="text-sm text-gray-500 truncate">
                {row.numReviewedDisputesAgainstReviewer} disputes against reviewer reviewed
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('numDisputesAgainstReviewer', {
        id: 'numDisputesAgainstReviewer',
        size: 260,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>
                # of Disputes Against Reviewer
              </DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const numDisputesAgainstReviewer = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Text className="text-sm max-h-[300px] overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap">
                {numDisputesAgainstReviewer}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('disputeDate', {
        id: 'disputeDate',
        size: 150,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Date</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const date = props.getValue();
          return (
            <Flex justify="start" align="center" className="h-full">
              {date ? format(new Date(date), 'MMM dd, yyyy HH:mm') : '-'}
            </Flex>
          );
        },
      }),
      columnHelper.accessor('languageCode', {
        id: 'languageCode',
        size: 180,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Language Code</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          return (
            <Flex justify="center" align="center" className="h-full">
              <Badge color="gray">{row.languageCode || 'N/A'}</Badge>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('category', {
        id: 'category',
        size: 180,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Category</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const category = props.getValue();
          const getBadgeColor = (cat: string) => {
            switch (cat) {
              case FeedbackDisputeCategory.FundamentalError:
              case FeedbackDisputeCategory.Incorrect:
                return 'error'; // Red - Most serious
              case FeedbackDisputeCategory.MisunderstoodGuidelines:
              case FeedbackDisputeCategory.FeedbackValidButUnfair:
                return 'warning'; // Orange - Reviewer error
              case FeedbackDisputeCategory.DisagreeButSubjective:
              case FeedbackDisputeCategory.UnhappyWithScore:
                return 'info'; // Blue - Score concern
              case FeedbackDisputeCategory.NotRelevant:
              case FeedbackDisputeCategory.Other:
                return 'gray';
              default:
                return 'gray';
            }
          };

          return (
            <Flex justify="center" align="center" className="h-full w-full px-1">
              <Badge color={getBadgeColor(category)} className="text-xs max-w-full truncate">
                {category}
              </Badge>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('justification', {
        id: 'justification',
        size: 220,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Justification</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const justification = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Text className="text-sm max-h-[300px] overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap">
                {justification}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('reviewerFeedback', {
        id: 'reviewerFeedback',
        size: 220,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Reviewer Feedback</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const reviewerFeedback = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Text className="text-sm max-h-[300px] overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap">
                {reviewerFeedback}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('taskAttemptLevel', {
        id: 'taskAttemptLevel',
        size: 200,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Task Attempt Level</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const taskAttemptLevel = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Badge color="gray">{taskAttemptLevel}</Badge>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('originalScore', {
        id: 'originalScore',
        size: 180,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Original Score</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const originalScore = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Badge color="gray">{originalScore}</Badge>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('suggestedScore', {
        id: 'suggestedScore',
        size: 180,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Suggested Score</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const suggestedScore = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Badge color="gray">{suggestedScore || 'N/A'}</Badge>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('supportingInstruction', {
        id: 'supportingInstruction',
        size: 200,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Supporting Instructions</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const supportingInstruction = props.getValue();
          return (
            <Flex justify="center" align="center" className="h-full">
              <Text className="text-sm max-h-[300px] overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap">
                {supportingInstruction}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        size: 150,
        enableColumnFilter: true,
        filterFn: 'stringEqualsSome',
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Status</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          const getStatusBadge = () => {
            if (row.status === DisputeStatus.CLOSED) {
              if (row.decision === DisputeDecision.ACCEPTED) {
                return <Badge color="success">Accepted</Badge>;
              } else if (row.decision === DisputeDecision.REJECTED) {
                return <Badge color="error">Rejected</Badge>;
              } else if (row.decision === DisputeDecision.SCORE_INVALIDATED) {
                return <Badge color="info">Score Invalidated</Badge>;
              } else if (row.decision === DisputeDecision.EXPIRED) {
                return <Badge color="neutral">Expired</Badge>;
              } else {
                return <Badge color="gray">Closed</Badge>;
              }
            }
            return <Badge color="warning">Pending</Badge>;
          };

          return (
            <Flex justify="center" align="center" className="h-full">
              {getStatusBadge()}
            </Flex>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        size: 200,
        enableColumnFilter: false,
        header: function headerComponent() {
          return (
            <div className="w-full">
              <DataTable.CellContentDefault>Actions</DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          const row = props.row.original;
          const isPending = row.status === DisputeStatus.OPEN;
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [shouldShowNewScoreSelector, setShouldShowNewScoreSelector] = useState(false);

          return (
            <div className="w-full h-full flex items-center justify-center">
              <Flex gap="1" wrap="wrap" className="max-w-full">
                {isPending && (
                  <>
                    <Tooltip
                      content="Invalidates the current score and accepts the suggested score, if it exists."
                      side="top"
                    >
                      <Button
                        size="1"
                        variant="outline"
                        color="success"
                        onClick={() => {
                          handleInvalidateDispute(
                            row.attemptId,
                            DisputeDecision.ACCEPTED,
                            Number(row.suggestedScore),
                          );
                        }}
                        disabled={isInvalidatingDispute}
                      >
                        Accept
                      </Button>
                    </Tooltip>
                    <Button
                      size="1"
                      variant="outline"
                      color="error"
                      onClick={() =>
                        handleInvalidateDispute(row.attemptId, DisputeDecision.REJECTED)
                      }
                      disabled={isInvalidatingDispute}
                    >
                      Reject
                    </Button>
                    <Tooltip
                      content="Invalidates the current score but does not accept the suggested score.
                              Use when score should not count but feedback is not incorrect."
                      side="bottom"
                    >
                      <Button
                        size="1"
                        variant="outline"
                        color="info"
                        onClick={() =>
                          handleInvalidateDispute(row.attemptId, DisputeDecision.SCORE_INVALIDATED)
                        }
                        disabled={isInvalidatingDispute}
                      >
                        Invalidate Score
                      </Button>
                    </Tooltip>
                    <Tooltip
                      content="Accepts the dispute and can give a new score higher than the original score."
                      side="bottom"
                    >
                      {Number(row.originalScore) < 5 && (
                        <Button
                          size="1"
                          variant="outline"
                          color="success"
                          onClick={() => setShouldShowNewScoreSelector(!shouldShowNewScoreSelector)}
                          disabled={isInvalidatingDispute}
                        >
                          New Score
                        </Button>
                      )}
                    </Tooltip>
                  </>
                )}
                <Button
                  size="1"
                  variant="outline"
                  onClick={() => handleAuditDispute(row)}
                  disabled={isAuditLoading}
                >
                  Audit
                </Button>
                {shouldShowNewScoreSelector && (
                  <div className="py-2 w-full">
                    <NewScoreSelector
                      attemptId={row.attemptId}
                      originalScore={Number(row.originalScore)}
                    />
                  </div>
                )}
              </Flex>
            </div>
          );
        },
      }),
    ],
    [projectId],
  );

  const filteredData = useMemo(() => {
    if (!globalFilter) return disputeRows;
    const searchTerm = globalFilter.toLowerCase();
    return disputeRows.filter(
      row =>
        row.attemptId.toLowerCase().includes(searchTerm) ||
        row.attempter.toLowerCase().includes(searchTerm) ||
        row.reviewer.toLowerCase().includes(searchTerm) ||
        (row.attempterName?.toLowerCase() || '').includes(searchTerm) ||
        (row.reviewerName?.toLowerCase() || '').includes(searchTerm) ||
        row.category.toLowerCase().includes(searchTerm) ||
        row.justification.toLowerCase().includes(searchTerm) ||
        row.status.toLowerCase().includes(searchTerm),
    );
  }, [disputeRows, globalFilter]);

  if (!enableFeedbackDisputes) {
    return (
      <Text>
        Feedback disputes are not enabled for this project. Please enable feedback disputes to see
        the disputes table.
      </Text>
    );
  }

  if (isLoading) {
    return <FunSkeleton />;
  }

  return (
    <>
      <DataTable.Root
        columns={columns}
        data={filteredData}
        enableRowSelection
        enableColumnFilters
        enableSorting
        onGlobalFilterChange={setGlobalFilter}
        initialState={{
          pagination: {
            pageSize: 25,
          },
          columnFilters: [
            { id: 'status', value: DisputeStatus.OPEN },
            {
              id: 'category',
              value: Object.values(FeedbackDisputeCategory),
            },
          ],
        }}
      >
        {({ table }) => (
          <>
            <DataTable.Actions>
              <DataTable.ActionsSearch placeholder="Search disputes..." size="2" />
              <DataTable.ActionsColumns />
              <DataTable.ActionsExport>
                <DataTable.ActionsExportItem rowModel="selected">
                  Export selected disputes as CSV
                </DataTable.ActionsExportItem>
                <DataTable.ActionsExportItem rowModel="filtered">
                  Export filtered disputes as CSV
                </DataTable.ActionsExportItem>
                <DataTable.ActionsExportItem>
                  Export all disputes as CSV
                </DataTable.ActionsExportItem>
              </DataTable.ActionsExport>
            </DataTable.Actions>
            <DataTable.Table>
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header}>
                        {header.column.getCanSort() ? (
                          <DataTable.ColumnHeaderSort header={header} />
                        ) : null}
                        {header.column.getCanFilter() ? (
                          <DataTable.ColumnHeaderFilter header={header} />
                        ) : null}
                      </DataTable.ColumnHeaderCell>
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
            <DataTable.Pagination>
              <DataTable.PaginationPageNavigation />
              <DataTable.PaginationPageSize />
              <DataTable.PaginationTotal />
            </DataTable.Pagination>
          </>
        )}
      </DataTable.Root>
    </>
  );
};
